import { useTranslation } from "react-i18next";
import { Link, Outlet } from "react-router";
import { AppNav } from "@/components/layout/app-nav";
import { PlanBanner } from "@/components/layout/plan-banner";
import { SiteHeader } from "@/components/layout/site-header";
import { useAuth } from "@/lib/session/auth-context";
import { env } from "@/lib/platform/env";
import { LEGAL_PAGES } from "@/lib/legal/operator";
import { useWorkspace } from "@/lib/session/workspace-context";

/**
 * Two shapes: signed out (a single centred column for the login and invitation pages) and signed in
 * (a 240px rail beside the work). The rail is the same colour as the canvas - a different colour
 * would split the screen into two worlds - and is separated by one hairline.
 */
export function Layout() {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const { workspace } = useWorkspace();
  const withNav = isAuthenticated && workspace !== null;

  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <div className="flex flex-1">
        {withNav && (
          <aside className="hidden w-60 shrink-0 border-r px-3 py-6 md:block">
            <AppNav />
          </aside>
        )}
        <main
          className={
            withNav
              ? "min-w-0 flex-1 px-4 py-8 lg:px-8"
              : "mx-auto w-full max-w-5xl flex-1 px-4 py-10"
          }
        >
          {withNav && <PlanBanner />}
          <Outlet />
        </main>
      </div>
      <footer className="space-y-2 border-t px-4 pt-5 pb-[calc(1.25rem+env(safe-area-inset-bottom))] text-center text-xs text-muted-foreground">
        <p>
          {env.appName} · {t("app.tagline")}
        </p>
        <nav
          className="flex flex-wrap justify-center gap-x-4 gap-y-1"
          aria-label={t("legal.nav")}
          data-testid="legal-links"
        >
          {LEGAL_PAGES.map((page) => (
            <Link key={page.key} to={page.path} className="hover:text-foreground">
              {t(`legal.${page.key}`)}
            </Link>
          ))}
        </nav>
      </footer>
    </div>
  );
}
